import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';

import history from '../../../history';
import { executeHttpRequest } from '../../../epics/api';
import { getErrorMessagesWithFieldNames } from '../../../utils';
import {
    TRebook,
    TFilterOptions,
    TRebooksProps,
    TPageSelectionState,
    TRebooksExtendingType,
    PAGE_SELECTION_STATE,
    REBOOKS_EXTENDING_TYPE,
    PAGE_SIZE_OPTIONS,
    DEFAULT_PAGE_SIZE,
} from './types';
import { mapDateParamsForApi, isWithinRecencyWindow } from './utils';

export const useRebooks = ({ notification }: TRebooksProps) => {
    const location = useLocation();
    const [content, setContent] = useState<TRebook[]>([]);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [filterOptions, setFilterOptions] = useState<TFilterOptions | null>(null);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
    // Selection state persists across paginated pages. We key by rebook.id and
    // store the free booking IDs from free_bookings since the extend API
    // expects those PKs.
    const [selectedRebooks, setSelectedRebooks] = useState<Map<number, number[]>>(
        () => new Map(),
    );
    const [extendingType, setExtendingType] =
        useState<TRebooksExtendingType>(null);
    const didInitDateRangeRef = useRef(false);
    const didApplyDefaultsRef = useRef(false);

    // Set default date range before the first render so DateDropDown's
    // constructor reads the correct URL params.
    if (!didInitDateRangeRef.current) {
        didInitDateRangeRef.current = true;
        const params = new URLSearchParams(history.location.search);
        if (!params.get('after') && !params.get('before')) {
            params.set('after', moment().startOf('isoWeek').format('YYYY-MM-DD'));
            params.set('before', moment().endOf('isoWeek').format('YYYY-MM-DD'));
            history.replace(`${history.location.pathname}?${params.toString()}`);
        }
    }

    const allowedSortColumns = useMemo(
        () =>
            new Set(
                (filterOptions?.ordering || []).map(item =>
                    item.value.replace(/^-/, ''),
                ),
            ),
        [filterOptions],
    );

    const bookingsAfter = useMemo(
        () => new URLSearchParams(location.search).get('after'),
        [location.search],
    );
    const bookingsBefore = useMemo(
        () => new URLSearchParams(location.search).get('before'),
        [location.search],
    );

    const fetchFilterOptions = useCallback(() => {
        if (!bookingsAfter || !bookingsBefore) {
            return;
        }

        const query = new URLSearchParams({
            bookings_after: bookingsAfter,
            bookings_before: bookingsBefore,
        });

        executeHttpRequest(`rebooks/filter-options/?${query.toString()}`, 'get')
            .then(response => {
                const data = response?.data?.content || null;
                setFilterOptions(data);

                const params = new URLSearchParams(history.location.search);
                const selectedConsultant = params.get('consultant');
                if (!selectedConsultant) {
                    return;
                }
                const stillValid = (data?.consultant || []).some(
                    (option: { value: number | null }) =>
                        option.value != null &&
                        String(option.value) === selectedConsultant,
                );
                if (!stillValid) {
                    params.delete('consultant');
                    params.set('page', '1');
                    history.replace(
                        `${history.location.pathname}?${params.toString()}`,
                    );
                    setCurrentPage(0);
                }
            })
            .catch(err => {
                const message = getErrorMessagesWithFieldNames(err);
                notification.error('Error', message);
            })
            .finally(() => setFilterOptionsLoaded(true));
    }, [bookingsAfter, bookingsBefore, notification]);

    const pushFiltersToUrl = useCallback((filters: Record<string, string>) => {
        // Read the live URL. Search is debounced, so the callback that fires
        // can be older than the date range and default ordering written after
        // the first render. Copying a stale search string drops those params
        // and the list request fails with "Parameter is required".
        const params = new URLSearchParams(history.location.search);
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                params.set(key, filters[key]);
            } else {
                params.delete(key);
            }
        });
        params.set('page', '1');
        history.push(`${history.location.pathname}?${params.toString()}`);
        setCurrentPage(0);
    }, []);

    const onFilterChange = useCallback(
        (id: string, value: any) => {
            if (id === 'dates') {
                const after = value?.after;
                const before = value?.before;
                if (after && before) {
                    pushFiltersToUrl({
                        after: moment(after).startOf('isoWeek').format('YYYY-MM-DD'),
                        before: moment(before).endOf('isoWeek').format('YYYY-MM-DD'),
                        consultant: '',
                    });
                }
            } else if (value && typeof value === 'object' && 'value' in value) {
                pushFiltersToUrl({ [id]: String(value.value) });
            } else if (value != null) {
                pushFiltersToUrl({ [id]: String(value) });
            }
        },
        [pushFiltersToUrl],
    );

    const fetchRebooks = useCallback(
        (pageIndex: number, searchParams: URLSearchParams, signal?: AbortSignal) => {
            const params = mapDateParamsForApi(searchParams);
            params.set('page', String(pageIndex + 1));
            if (!params.get('paginate_by')) {
                params.set('paginate_by', String(DEFAULT_PAGE_SIZE));
            }

            setLoading(true);

            return executeHttpRequest(
                `rebooks/?${params.toString()}`,
                'get',
                null,
                true,
                { signal },
            )
                .then(response => {
                    setContent(response.data?.content || []);
                    setTotalCount(response.data?.meta?.count ?? null);
                    setLoading(false);
                })
                .catch(err => {
                    if (err?.code === 'ERR_CANCELED') {
                        return;
                    }
                    const message = getErrorMessagesWithFieldNames(err);
                    notification.error('Error', message);
                    setLoading(false);
                });
        },
        [notification],
    );

    const onPageChange = useCallback(
        (pageIndex: number) => {
            const params = new URLSearchParams(location.search);
            if (pageIndex >= 1) {
                params.set('page', String(pageIndex + 1));
            } else {
                params.delete('page');
            }
            history.push(`${location.pathname}?${params.toString()}`);
            setCurrentPage(pageIndex);
        },
        [location.pathname, location.search],
    );

    const onSortedChange = useCallback(
        (newSorted: { id: string; desc: boolean }[]) => {
            const params = new URLSearchParams(location.search);
            const primarySort = newSorted[0];
            if (primarySort && allowedSortColumns.has(primarySort.id)) {
                params.set('ordering', `${primarySort.desc ? '-' : ''}${primarySort.id}`);
                params.set('page', '1');
                setCurrentPage(0);
                history.push(`${location.pathname}?${params.toString()}`);
            } else if (!primarySort) {
                params.delete('ordering');
                params.set('page', '1');
                setCurrentPage(0);
                history.push(`${location.pathname}?${params.toString()}`);
            }
        },
        [allowedSortColumns, location.pathname, location.search],
    );

    const onPageSizeChange = useCallback(
        (newSize: number) => {
            const params = new URLSearchParams(location.search);
            params.set('paginate_by', String(newSize));
            params.set('page', '1');
            setCurrentPage(0);
            history.push(`${location.pathname}?${params.toString()}`);
        },
        [location.pathname, location.search],
    );

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // Single effect handles both default-ordering bootstrap and data fetching.
    // On first load after filter options arrive, if a default ordering needs to
    // be written to the URL we do that and return early; the resulting
    // location.search change re-triggers this effect for the actual fetch.
    // This avoids the double-fetch that occurred when the two concerns lived in
    // separate effects.
    useEffect(() => {
        if (!filterOptionsLoaded) {
            return undefined;
        }

        const params = new URLSearchParams(location.search);

        if (!didApplyDefaultsRef.current) {
            didApplyDefaultsRef.current = true;
            const defaults = filterOptions?.defaults ?? {};
            const missing = Object.entries(defaults).filter(
                ([key, value]) => value && !params.get(key),
            );
            if (missing.length > 0) {
                missing.forEach(([key, value]) => {
                    if (value) {
                        params.set(key, value);
                    }
                });
                history.replace(`${location.pathname}?${params.toString()}`);
                return undefined;
            }
        }

        const pageParam = Number(params.get('page'));
        const pageIndex = pageParam && pageParam > 0 ? pageParam - 1 : 0;
        setCurrentPage(pageIndex);

        const controller = new AbortController();
        fetchRebooks(pageIndex, params, controller.signal);
        return () => controller.abort();
    }, [fetchRebooks, filterOptions, filterOptionsLoaded, location.pathname, location.search]);

    const currentUrlParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );

    const pageSize = useMemo(() => {
        const size = Number(currentUrlParams.get('paginate_by'));
        return PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE;
    }, [currentUrlParams]);

    const pages = totalCount !== null ? Math.ceil(totalCount / pageSize) : -1;

    const defaultSorted = useMemo(() => {
        const ordering = currentUrlParams.get('ordering');
        if (!ordering) {
            return [];
        }
        const desc = ordering.startsWith('-');
        const id = desc ? ordering.substring(1) : ordering;
        return [{ id, desc }];
    }, [currentUrlParams]);

    // Recency window: the BE accepts source weeks from the current week back to
    // 2 weeks ago. Future weeks and weeks older than 2 weeks are rejected.
    // Computed from the URL's `after` param.
    const isRangeExtendable = useMemo(() => {
        const after = currentUrlParams.get('after');
        return after ? isWithinRecencyWindow(after) : false;
    }, [currentUrlParams]);

    // Only rebooks where the freelancer is free next week can be extended,
    // and only when the selected date range falls within the recency window.
    const isRebookSelectable = useCallback(
        (rebook: TRebook) =>
            isRangeExtendable &&
            (rebook.free_bookings?.length ?? 0) > 0,
        [isRangeExtendable],
    );

    const isRebookSelected = useCallback(
        (rebook: TRebook) => selectedRebooks.has(rebook.id),
        [selectedRebooks],
    );

    const toggleRebook = useCallback(
        (rebook: TRebook) => {
            if (!isRebookSelectable(rebook)) {
                return;
            }
            setSelectedRebooks(prev => {
                const next = new Map(prev);
                if (next.has(rebook.id)) {
                    next.delete(rebook.id);
                } else {
                    next.set(rebook.id, (rebook.free_bookings ?? []).map(b => b.id));
                }
                return next;
            });
        },
        [isRebookSelectable],
    );

    const selectableOnPage = useMemo(
        () => content.filter(isRebookSelectable),
        [content, isRebookSelectable],
    );

    const pageSelectionState = useMemo<TPageSelectionState>(() => {
        if (selectableOnPage.length === 0) {
            return PAGE_SELECTION_STATE.NONE;
        }
        const selectedOnPageCount = selectableOnPage.reduce(
            (count, r) => count + (selectedRebooks.has(r.id) ? 1 : 0),
            0,
        );
        if (selectedOnPageCount === 0) return PAGE_SELECTION_STATE.NONE;
        if (selectedOnPageCount === selectableOnPage.length) return PAGE_SELECTION_STATE.ALL;
        return PAGE_SELECTION_STATE.SOME;
    }, [selectableOnPage, selectedRebooks]);

    const selectAllOnPage = useCallback(() => {
        setSelectedRebooks(prev => {
            const next = new Map(prev);
            selectableOnPage.forEach(r => next.set(r.id, (r.free_bookings ?? []).map(b => b.id)));
            return next;
        });
    }, [selectableOnPage]);

    const deselectAllOnPage = useCallback(() => {
        setSelectedRebooks(prev => {
            const next = new Map(prev);
            content.forEach(r => next.delete(r.id));
            return next;
        });
    }, [content]);

    const clearSelection = useCallback(() => {
        setSelectedRebooks(new Map());
    }, []);

    const selectedBookingCount = useMemo(
        () =>
            Array.from(selectedRebooks.values()).reduce(
                (sum, bookingIds) => sum + bookingIds.length,
                0,
            ),
        [selectedRebooks],
    );

    const togglePageSelection = useCallback(() => {
        if (pageSelectionState === PAGE_SELECTION_STATE.ALL) {
            deselectAllOnPage();
        } else {
            selectAllOnPage();
        }
    }, [pageSelectionState, selectAllOnPage, deselectAllOnPage]);

    const reloadData = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const pageParam = Number(params.get('page'));
        const pageIndex = pageParam && pageParam > 0 ? pageParam - 1 : 0;
        return fetchRebooks(pageIndex, params);
    }, [fetchRebooks, location.search]);

    const extendAll = useCallback(() => {
        // Scope "extend all" to the currently active date range (range mode).
        // Both bookings_after and bookings_before are required by the API.
        const params = new URLSearchParams(location.search);
        const after = params.get('after');
        const before = params.get('before');
        if (!after || !before) {
            notification.error('Error', 'Please select a date range before extending all rebooks.');
            return Promise.resolve();
        }
        if (!isWithinRecencyWindow(after)) {
            notification.error(
                'Error',
                'Only the current week and up to 2 weeks back can be extended.',
            );
            return Promise.resolve();
        }
        setExtendingType(REBOOKS_EXTENDING_TYPE.ALL);
        const body: { bookings: number[]; bookings_after: string; bookings_before: string } = {
            bookings: [],
            bookings_after: after,
            bookings_before: before,
        };
        return executeHttpRequest('rebooks/extend/', 'post', body)
            .then(response => {
                const detail = response?.data?.meta?.detail;
                const count = response?.data?.meta?.bookings_count ?? 0;
                notification.success(
                    'Scheduled',
                    detail ||
                        `${count} rebook${
                            count === 1 ? '' : 's'
                        } queued. You'll receive an email report when done.`,
                );
                clearSelection();
                reloadData();
                return response;
            })
            .catch(err => {
                const message = getErrorMessagesWithFieldNames(err);
                notification.error('Error', message);
            })
            .finally(() => setExtendingType(null));
    }, [notification, clearSelection, reloadData, location.search]);

    const extendSelected = useCallback(() => {
        if (selectedRebooks.size === 0) {
            return Promise.resolve();
        }
        setExtendingType(REBOOKS_EXTENDING_TYPE.SELECTED);
        const bookings = Array.from(selectedRebooks.values()).flat();
        return executeHttpRequest('rebooks/extend/', 'post', {
            bookings,
        })
            .then(response => {
                const detail = response?.data?.meta?.detail;
                const count = response?.data?.meta?.bookings_count ?? bookings.length;
                notification.success(
                    'Scheduled',
                    detail ||
                        `${count} rebook${
                            count === 1 ? '' : 's'
                        } queued. You'll receive an email report when done.`,
                );
                clearSelection();
                reloadData();
                return response;
            })
            .catch(err => {
                const message = getErrorMessagesWithFieldNames(err);
                notification.error('Error', message);
            })
            .finally(() => setExtendingType(null));
    }, [notification, selectedRebooks, clearSelection, reloadData]);

    return {
        content,
        totalCount,
        pages,
        pageSize,
        loading,
        currentPage,
        allowedSortColumns,
        currentUrlParams,
        defaultSorted,
        filterOptions,
        filterOptionsLoaded,
        onFilterChange,
        onPageChange,
        onPageSizeChange,
        onSortedChange,
        selectedCount: selectedRebooks.size,
        selectedBookingCount,
        pageSelectionState,
        selectableOnPageCount: selectableOnPage.length,
        isRebookSelected,
        isRebookSelectable,
        toggleRebook,
        togglePageSelection,
        selectAllOnPage,
        deselectAllOnPage,
        clearSelection,
        extendingType,
        isRangeExtendable,
        extendAll,
        extendSelected,
    };
};
